import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Stack,
} from '@mui/material';

import { IncidentCodeApi, DataApi } from '@/api';
import { Page, PageHeader } from '@/components';
import { IIncidentCode, IIncidentCodePayload } from '@/models/incident.code';
import { ConfirmTypes, useToastStore, useDataStore } from '@/store';

import { IncidentCodeForm } from './components/IncidentCodeForm';

const FORM_ID = 'incident-code-edit-form';

export const EditIncidentCodePage = () => {
  const navigate = useNavigate();
  const { incidentCodeId } = useParams<{
    incidentCodeId: string;
  }>();
  const { updateToast } = useToastStore();
  const { updateData } = useDataStore();

  const [incidentCodeData, setIncidentCodeData] =
    useState<IIncidentCode | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmittingViaPage, setIsSubmittingViaPage] = useState(false);

  useEffect(() => {
    if (!incidentCodeId) {
      setError('Incident Code ID is missing.');
      setLoading(false);
      return;
    }
    const fetchIncidentCode = async () => {
      setLoading(true);
      try {
        const response = await IncidentCodeApi.getById(incidentCodeId);
        setIncidentCodeData(response);
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to load incident code details.');
        updateToast({
          type: ConfirmTypes.ERROR,
          message: err.message || 'Failed to load details.',
          open: true,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchIncidentCode();
  }, [incidentCodeId, updateToast]);

  const handleSubmitFromForm = async (
    data: IIncidentCodePayload,
  ): Promise<void> => {
    if (!incidentCodeId) {
      updateToast({
        type: ConfirmTypes.ERROR,
        message: 'Incident Code ID is missing.',
        open: true,
      });
      return Promise.reject(new Error('Missing required information'));
    }
    setIsSubmittingViaPage(true);
    try {
      await IncidentCodeApi.update(incidentCodeId, data);

      // Refresh incident codes in the data store
      try {
        const response = await DataApi.getIncidentCodes();
        if (response.data) {
          updateData({ incidentCodes: response.data });
        }
      } catch (refreshError) {
        updateToast({
          open: true,
          message: 'Failed to refresh incident codes',
        });
      }

      updateToast({
        type: ConfirmTypes.SUCCESS,
        message: 'Incident Code has been updated successfully',
        open: true,
      });
      navigate(
        `/admin/account-configuration/incident-codes/view/${incidentCodeId}${window.location.search}`,
      );
    } catch (err: any) {
      updateToast({
        type: ConfirmTypes.ERROR,
        message: err.message || 'Failed to update incident code.',
        open: true,
      });
      throw err;
    } finally {
      setIsSubmittingViaPage(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleSave = () => {
    const form = document.getElementById(FORM_ID);
    if (form) {
      form.dispatchEvent(
        new Event('submit', { cancelable: true, bubbles: true }),
      );
    } else {
      updateToast({
        open: true,
        message: 'Could not find form to submit.',
        type: ConfirmTypes.ERROR,
      });
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 3,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Page title="Error">
        <PageHeader
          title="Error Loading Incident Code"
          backRoute="/admin/incident-codes"
        />
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error">{error}</Typography>
          <Button onClick={handleCancel} sx={{ mt: 2 }}>
            Go Back
          </Button>
        </Box>
      </Page>
    );
  }

  if (!incidentCodeData) {
    return (
      <Page title="Not Found">
        <PageHeader
          title="Incident Code Not Found"
          backRoute="/admin/incident-codes"
        />
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography>
            The requested incident code could not be found.
          </Typography>
          <Button onClick={handleCancel} sx={{ mt: 2 }}>
            Go Back
          </Button>
        </Box>
      </Page>
    );
  }

  const pageTitle = 'Edit Incident Code';

  return (
    <Page
      title={pageTitle}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <PageHeader
        title={pageTitle}
        onBack={handleCancel}
        action={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleCancel}
              disabled={isSubmittingViaPage}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleSave}
              disabled={isSubmittingViaPage}
            >
              {isSubmittingViaPage ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Save Changes'
              )}
            </Button>
          </Stack>
        }
      />
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: { xs: 2, md: 3 } }}>
        {incidentCodeData ? (
          <IncidentCodeForm
            formId={FORM_ID}
            onSubmit={handleSubmitFromForm}
            initialData={incidentCodeData}
            isEditMode={true}
          />
        ) : (
          !loading && (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography>Loading form data...</Typography>
            </Box>
          )
        )}
      </Box>
    </Page>
  );
};

export default EditIncidentCodePage;
